import React, { useState, useEffect, useRef, useId } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    InputBase,
    Dialog,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

const SearchContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : '#FAFAF9',
    borderRadius: '7px',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.text.secondary,
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#FFFFFF',
    },
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : '#FFFFFF',
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
    flex: 1,
    paddingLeft: '32px',
    paddingRight: '44px',
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
    '& input': {
        padding: '6px 0',
        height: '20px',
        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 0.8,
        },
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    position: 'absolute',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    color: theme.palette.text.secondary,
    '& svg': {
        fontSize: '1.05rem',
    },
}));

const ShortcutKbd = styled('kbd')(({ theme }) => ({
    position: 'absolute',
    right: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px 5px',
    fontSize: '0.6875rem',
    fontFamily: 'inherit',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : '#FFFFFF',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    pointerEvents: 'none',
    userSelect: 'none',
}));

const defaultSearchableItems = [];

const NavSearch = ({ value, onChange, searchableItems = defaultSearchableItems }) => {
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [paletteQuery, setPaletteQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef(null);
    const navigate = useNavigate();
    const titleId = useId();

    const isMac = typeof window !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
    const shortcutText = isMac ? '⌘K' : 'Ctrl+K';

    const handleOpenPalette = () => {
        setPaletteOpen(true);
        setPaletteQuery('');
        setSelectedIndex(0);
    };

    const handleClosePalette = () => {
        setPaletteOpen(false);
        setPaletteQuery('');
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setPaletteOpen((prev) => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const filteredPaletteItems = paletteQuery.trim() === ''
        ? searchableItems.slice(0, 10)
        : searchableItems.filter((item) => {
            const q = paletteQuery.toLowerCase();
            return (
                item.title.toLowerCase().includes(q) ||
                (item.section && item.section.toLowerCase().includes(q))
            );
        }).slice(0, 12);

    const handleItemSelect = (item) => {
        if (!item || !item.to) return;
        handleClosePalette();
        if (typeof item.to === 'string') {
            navigate(item.to);
        } else if (item.to.pathname) {
            navigate(`${item.to.pathname}${item.to.search || ''}`);
        }
    };

    const handlePaletteKeyDown = (e) => {
        const count = filteredPaletteItems.length;
        if (count === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % count);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + count) % count);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredPaletteItems[selectedIndex]) {
                handleItemSelect(filteredPaletteItems[selectedIndex]);
            }
        }
    };

    return (
        <>
            <SearchContainer>
                <SearchIconWrapper>
                    <SearchIcon />
                </SearchIconWrapper>
                <StyledInput
                    placeholder="Filter navigation"
                    value={value}
                    inputProps={{ 'aria-label': 'Filter navigation' }}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleOpenPalette();
                        }
                    }}
                />
                <ShortcutKbd role="button" onClick={handleOpenPalette}>
                    {shortcutText}
                </ShortcutKbd>
            </SearchContainer>

            <Dialog
                fullWidth
                open={paletteOpen}
                maxWidth="sm"
                aria-labelledby={titleId}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        overflow: 'hidden',
                        mt: '10vh',
                        verticalAlign: 'top',
                    },
                }}
                onClose={handleClosePalette}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: '12px 16px',
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: '1.25rem' }} />
                    <InputBase
                        autoFocus
                        fullWidth
                        placeholder="Go to a screen or resource..."
                        value={paletteQuery}
                        sx={{ fontSize: '0.95rem' }}
                        onChange={(e) => {
                            setPaletteQuery(e.target.value);
                            setSelectedIndex(0);
                        }}
                        onKeyDown={handlePaletteKeyDown}
                    />
                    <Chip
                        label="ESC"
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                        onClick={handleClosePalette}
                    />
                </Box>

                <List
                    ref={listRef}
                    sx={{
                        p: '6px 8px',
                        maxHeight: '380px',
                        overflowY: 'auto',
                    }}
                >
                    {filteredPaletteItems.length > 0 ? (
                        filteredPaletteItems.map((item, index) => {
                            const isSelected = index === selectedIndex;
                            return (
                                <ListItemButton
                                    key={`${item.section}-${item.title}-${item.to}`}
                                    selected={isSelected}
                                    sx={{
                                        borderRadius: '8px',
                                        py: '8px',
                                        px: '12px',
                                        my: '2px',
                                        '&.Mui-selected': {
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                            '&:hover': {
                                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                                            },
                                        },
                                    }}
                                    onClick={() => handleItemSelect(item)}
                                >
                                    {item.icon ? (
                                        <ListItemIcon
                                            sx={{
                                                minWidth: '34px',
                                                color: (theme) => theme.palette.primary.main,
                                                '& svg': { fontSize: '1.2rem' },
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                    ) : null}
                                    <ListItemText
                                        primary={item.title}
                                        primaryTypographyProps={{
                                            fontSize: '0.875rem',
                                            fontWeight: isSelected ? 600 : 400,
                                        }}
                                    />
                                    {item.section ? (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                                ml: 1,
                                            }}
                                        >
                                            {item.section}
                                        </Typography>
                                    ) : null}
                                </ListItemButton>
                            );
                        })
                    ) : (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">No navigation destinations match &quot;{paletteQuery}&quot;</Typography>
                        </Box>
                    )}
                </List>
            </Dialog>
        </>
    );
};

NavSearch.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    searchableItems: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            section: PropTypes.string,
            to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
            icon: PropTypes.node,
        }),
    ),
};

export default NavSearch;
